import * as angular from 'angular';
import { Decimal } from 'decimal.js';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpMaxQuantityValidator
     * @memberof app
     *
     * @restrict A
     * @requires ngModel.NgModelController
     * @requires pkpOrderCurve
     *
     * @description
     * Validate against the max quantity that can be passed as either a string, number or expression.
     * If this is used within an order curve, it validates upon changes in the order curve.
     * If the validator is used within an order curve (parent has {@link pkpOrderCurve}) directive and is not
     * the first curve point in the curve, it validates upon changes in the order curve point that precedes
     * this one.
     * By passing the amount of the preceding field to the validator, you can ensure that the curve is monotonically
     * decreasing.
     *
     * @attr {PkpUseOriginals} pkpUseOriginals
     *
     * @example
     // Withing an ngRepeat
     <input type="number" class="form-control" id="amount" ng-model="order.quantity"
            name="orderQuantity{{$index}}" pkp-use-originals
            ng-change="onAnyAmountChange({orderCurve: bid.orderCurve})"
            pkp-quantity-converter="auction.networkPoint.physicalUnit"
            pkp-min-quantity-validator="1"
            pkp-max-quantity-validator="$first ? auction.marketable.originalValue.value : bid.orderCurve[$index - 1].quantity.originalValue.value"
            pkp-no-decimal-validator>
     *
     * @see {@link pkpMaxQuantityValidator#onPrecedingCurvePointChange}
     * @author flzemke.btc
     */
    angular
        .module('app')
        .directive('pkpMaxQuantityValidator', pkpMaxQuantityValidator);

    function pkpMaxQuantityValidator(fieldKeyService) {
        return {
            restrict: 'A',
            scope: false,
            require: ['ngModel', '?^^pkpOrderCurve'],
            link: linkFn
        };

        function linkFn(scope, elem, attr, requiredControllers) {
            var ngModelCtrl = requiredControllers[0];
            var pkpOrderCurveCtrl = requiredControllers[1];
            var fieldKey = fieldKeyService.initialize(attr.pkpUseOriginals);

            (function init() {
                onPrecedingCurvePointChange();
            })();

            /**
             * If this validator is part of an order curve and it's not the first in the curve, the validator will be
             * triggered whenever the preceding curve point is changed.
             */
            function onPrecedingCurvePointChange() {
                if (!pkpOrderCurveCtrl) {
                    return;
                }

                scope.$watch(
                    function () {
                        return pkpOrderCurveCtrl.orderCurve;
                    },
                    function (newOrderCurve) {
                        if (!newOrderCurve) {
                            return;
                        }

                        ngModelCtrl.$validate();
                    },
                    true
                );
            }

            ngModelCtrl.$validators.pkpMaxQuantityValidator = function (
                modelValue
            ) {
                if (
                    !attr.pkpMaxQuantityValidator ||
                    !modelValue ||
                    !modelValue[fieldKey.valueKey] ||
                    ngModelCtrl.$isEmpty(modelValue[fieldKey.valueKey].value)
                ) {
                    return true;
                }

                try {
                    var maxQuantity;
                    if (isNaN(attr.pkpMaxQuantityValidator)) {
                        var evaluatedPkpMaxQuantityValidator = scope.$eval(
                            attr.pkpMaxQuantityValidator
                        );

                        if (
                            !evaluatedPkpMaxQuantityValidator &&
                            evaluatedPkpMaxQuantityValidator !== 0
                        ) {
                            return true;
                        }

                        maxQuantity = new Decimal(
                            evaluatedPkpMaxQuantityValidator
                        );
                    } else {
                        maxQuantity = attr.pkpMaxQuantityValidator;
                    }

                    // If there is no maxQuantity defined, the validation defaults to true.
                    if (angular.isUndefined(maxQuantity)) {
                        return true;
                    }

                    if (
                        new Decimal(
                            modelValue[fieldKey.valueKey].value
                        ).greaterThan(new Decimal(maxQuantity))
                    ) {
                        return false;
                    }
                } catch (e) {
                    return false;
                }

                return true;
            };

            attr.$observe('pkpMaxQuantityValidator', function () {
                ngModelCtrl.$validate();
            });
        }
    }
})();
